import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";

import HelpPopover from "./common/Popover";
import UpdateMenu from "./common/UpdateMenu";
import CustomChip from "components/common/Chip";
import CustomButton from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import TopHeader from "components/common/TopHeader";
import RiveAnimation from "components/common/RiveAnimation";
import {
  CardExpiryWarning,
  getPaymentChipIconColor,
  getPaymentChipColor,
  getPaymentChipLabel,
} from "components/Screens/Customers/utils";
import { usePermissions } from "components/Hook";

import { getPlatformConfig } from "selectors/platformConfig";
import { getUserAdditionalInfo, getLoadingAdditionalInfo } from "selectors/user";
import { baseUrl } from "util/APIBaseUrl";

import { CustomerBankInfo } from "store/actions/Payment/GetCustomerBankInfo";
import { ResetAddCustomerBankInfo } from "store/actions/Payment/AddBankAccountInfo";
import { showSnackbar } from "store/actions/Utility";
import { AlertType } from "components/common/Snackbar";
import {
  createFinancialConnectionsSetupIntent,
  finalizeFinancialConnectionsSetup,
  getPaymentMethods,
  setDefaultPaymentMethod,
  detachPaymentMethod,
} from "services/customer";

import {
  HelpOutlineOutlined as HelpIcon,
  AccountBalanceOutlined as AccountBalanceIcon,
  CreditCardOutlined as CreditCardIcon,
  ChevronRightOutlined as ChevronRightIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import VisaCardIcon from "assets/svgs/VisaCardIcon";
import PaymentWorldMap from "assets/svgs/PaymentWorldMap";
import MasterCardIcon from "assets/svgs/MasterCardIcon";
import AmericanExpressCardIcon from "assets/svgs/AmericanExpressCardIcon";

import "./Payment.css";

function renderPaymentCard(brand) {
  switch (brand) {
    case "amex":
      return <AmericanExpressCardIcon />;
    case "visa":
      return <VisaCardIcon />;
    default:
      return <MasterCardIcon />;
  }
}

function PaymentMethods() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loggedInUserType } = usePermissions();
  const { stripeFc = false } = useFlags();

  const platformConfig = useSelector(getPlatformConfig);
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const loadingUserInfo = useSelector(getLoadingAdditionalInfo);

  const [anchorEl, setAnchorEl] = useState(null);
  const [stripeFcLoading, setStripeFcLoading] = useState(false);
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [loadingPMs, setLoadingPMs] = useState(false);
  const [pmMenuAnchor, setPmMenuAnchor] = useState(null);
  const [selectedPm, setSelectedPm] = useState(null);

  const openInfo = Boolean(anchorEl);
  const id = openInfo ? "simple-popover" : undefined;

  const { payload: platformPayload = {}, ok: isPlatformConfigLoaded = false } = platformConfig || {};

  const loadingBankAccountInfo = useSelector((globalState) => globalState.GetCustomerBankInfo.loadingBankAccountInfo);
  const loadingplatformConfig = useSelector((globalState) => globalState.PlatformConfig.loadingplatformConfig);
  const bankAccountsDetails = useSelector((state) => state.GetCustomerBankInfo.bankAccountInfo);
  const addBankAccountInfo = useSelector((globalState) => globalState.AddBankAccountInfo.addBankAccountInfo);
  const loadingAddBankAccountInfo = useSelector(
    (globalState) => globalState.AddBankAccountInfo.loadingAddBankAccountInfo
  );

  const isLoading = loadingplatformConfig || !isPlatformConfigLoaded;
  const isMultiPm = paymentMethodsList.length > 0;
  const otherPms = paymentMethodsList.filter((pm) => !pm.isDefault);

  const fetchPaymentMethods = useCallback(async () => {
    if (!userAdditionalInfo?.customerId) return;
    try {
      setLoadingPMs(true);
      const result = await getPaymentMethods(userAdditionalInfo.customerId);
      setPaymentMethodsList(result?.paymentMethods || []);
    } catch {
      setPaymentMethodsList([]);
    } finally {
      setLoadingPMs(false);
    }
  }, [userAdditionalInfo?.customerId]);

  const refreshAll = useCallback(() => {
    dispatch(CustomerBankInfo({ request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/bankInfo` }));
    fetchPaymentMethods();
  }, [dispatch, userAdditionalInfo?.customerId, fetchPaymentMethods]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleCloseAnchor = () => setAnchorEl(null);

  const handlePmMenuOpen = (event, pm) => {
    event.stopPropagation();
    setPmMenuAnchor(event.currentTarget);
    setSelectedPm(pm);
  };
  const handlePmMenuClose = () => {
    setPmMenuAnchor(null);
    setSelectedPm(null);
  };

  const handleMakeDefault = async () => {
    handlePmMenuClose();
    try {
      await setDefaultPaymentMethod(userAdditionalInfo.customerId, selectedPm.id);
      refreshAll();
      dispatch(showSnackbar({ dispatch, type: AlertType.success, message: "Default payment method updated." }));
    } catch {
      dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to update default payment method." }));
    }
  };

  const handleDetach = async () => {
    handlePmMenuClose();
    try {
      await detachPaymentMethod(userAdditionalInfo.customerId, selectedPm.id);
      fetchPaymentMethods();
      dispatch(showSnackbar({ dispatch, type: AlertType.success, message: "Payment method removed." }));
    } catch {
      dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to remove payment method." }));
    }
  };

  const handleConnectViaStripeFC = useCallback(async () => {
    try {
      if (!platformPayload.STRIPE_PUBLISHABLE_KEY) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Stripe configuration is missing." }));
        return;
      }
      setStripeFcLoading(true);
      const stripe = await loadStripe(platformPayload.STRIPE_PUBLISHABLE_KEY);
      const fcResponse = await createFinancialConnectionsSetupIntent(userAdditionalInfo?.customerId);
      const fcClientSecret = fcResponse?.clientSecret || fcResponse?.client_secret;
      if (!fcClientSecret) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to initialize Stripe." }));
        setStripeFcLoading(false);
        return;
      }
      setStripeFcLoading(false);
      const currentUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      const companyName = userAdditionalInfo?.companyName || currentUser?.name || "Customer";
      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: fcClientSecret,
        params: {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: { name: companyName, email: currentUser?.email || "" },
          },
        },
      });
      if (error) {
        dispatch(showSnackbar({ dispatch, type: AlertType.error, message: error.message || "Failed to connect." }));
        return;
      }
      if (setupIntent && setupIntent.payment_method) {
        setStripeFcLoading(true);
        const result = await finalizeFinancialConnectionsSetup(userAdditionalInfo?.customerId, setupIntent.id);
        if (result?.success) {
          dispatch(showSnackbar({ dispatch, type: AlertType.success, message: "Payment method added!" }));
          setStripeFcLoading(false);
          refreshAll();
        } else {
          dispatch(showSnackbar({ dispatch, type: AlertType.error, message: "Failed to finalize connection." }));
          setStripeFcLoading(false);
        }
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: err[0]?.description || err?.message || "Something went wrong.",
        })
      );
      setStripeFcLoading(false);
    }
  }, [
    platformPayload.STRIPE_PUBLISHABLE_KEY,
    userAdditionalInfo?.customerId,
    userAdditionalInfo?.companyName,
    dispatch,
    refreshAll,
  ]);

  const handlePaymentMethodClick = (paymentType, update, change) => {
    if (update) navigate(`/main/payment/add-payment-method?type=${paymentType}&update=true`);
    else if (change) navigate(`/main/payment/add-payment-method?type=${paymentType}&change=true`);
    else navigate(`/main/payment/add-payment-method?type=${paymentType}`);
  };

  useEffect(() => {
    if (!userAdditionalInfo?.customerId) return;
    if (addBankAccountInfo?.ok) refreshAll();
  }, [addBankAccountInfo, refreshAll, userAdditionalInfo?.customerId]);

  useEffect(() => {
    if (!userAdditionalInfo?.customerId) return;
    dispatch(ResetAddCustomerBankInfo({}));
    dispatch(CustomerBankInfo({ request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/bankInfo` }));
    fetchPaymentMethods();
  }, [userAdditionalInfo?.customerId, dispatch, fetchPaymentMethods]);

  if (loggedInUserType !== "customer" || !isPlatformConfigLoaded) return null;

  const paymentTypes = {
    ach: true,
    creditCard: userAdditionalInfo?.creditCardSettings?.allowCreditCard,
    achData: bankAccountsDetails?.paymentMethodType === "ACH",
    creditCardData: bankAccountsDetails?.paymentMethodType === "CREDIT_CARD",
  };

  const hasDefaultPm = paymentTypes?.achData || paymentTypes?.creditCardData;

  return (
    <>
      <Helmet>
        <title>Edge | Payment Methods</title>
      </Helmet>
      {(isLoading ||
        loadingAddBankAccountInfo ||
        loadingBankAccountInfo ||
        loadingplatformConfig ||
        loadingUserInfo ||
        loadingPMs ||
        stripeFcLoading) && <PageLoader />}

      <div className="payment-container">
        <TopHeader
          title="Payment method"
          secondaryBtn="Need help?"
          secondaryBtnIcon={<HelpIcon className="button-secondary-icon" />}
          secondaryBtnClick={handleClick}
          secondaryBtnVariant="text"
        />

        <HelpPopover id={id} open={openInfo} anchorEl={anchorEl} onClose={handleCloseAnchor} />

        <div className="payment-content-container">
          {hasDefaultPm && !loadingUserInfo ? (
            <div className="flex flex-col gap-1 w-full">
              {/* Default PM — big card */}
              <div className="payment-world-map-container p-1">
                <div className="payment-world-map">
                  <PaymentWorldMap />
                </div>
                {paymentTypes.achData ? (
                  <div className="flex flex-col gap-05 bank-account-text">
                    <div className="flex items-center gap-025">
                      <AccountBalanceIcon className="payment-icon" />
                      <p className="para-body-s-medium color-text-body-primary">ACH</p>
                    </div>
                    <p className="heading-h2-medium">
                      {bankAccountsDetails?.bankName}{" "}
                      <span className="color-text-body-secondary">•••• {bankAccountsDetails?.last4}</span>
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-05 bank-account-text">
                    <div className="flex items-center gap-025">
                      <CreditCardIcon className="payment-icon" />
                      <p className="para-body-s-medium color-text-body-primary">Credit/Debit Card</p>
                    </div>
                    <p className="heading-h2-medium flex items-center capitalize z-1 gap-05">
                      {renderPaymentCard(bankAccountsDetails?.brand)} {bankAccountsDetails?.brand}{" "}
                      <span className="color-text-body-secondary">•••• {bankAccountsDetails?.last4}</span>
                    </p>
                    <CardExpiryWarning
                      expiry={bankAccountsDetails?.expiry}
                      warningDays={platformPayload?.CREDIT_CARD_EXPIRY_WARNING_DAYS}
                    />
                  </div>
                )}
                <div className="update-menu-wrapper">
                  <UpdateMenu
                    isAch={paymentTypes.achData}
                    creditAllowed={paymentTypes?.creditCard}
                    onUpdateClick={() => handlePaymentMethodClick(paymentTypes.achData ? "ach" : "card", true)}
                    onChangePaymentMethodClick={() =>
                      handlePaymentMethodClick(paymentTypes.achData ? "card" : "ach", false, true)
                    }
                  />
                </div>
              </div>

              <div className="border-b" />

              {/* Next payment info */}
              <div className="flex justify-between items-center">
                <p className="para-body-m-medium color-text-body-primary">
                  Next payment on {moment(bankAccountsDetails?.currentPayroll?.Payroll_Month).format("MMM D")}
                </p>
                <CustomChip
                  size="small"
                  onClick={() => navigate("/main/payroll")}
                  color={getPaymentChipColor(bankAccountsDetails?.currentPayroll?.Status)}
                  label={getPaymentChipLabel(bankAccountsDetails?.currentPayroll?.Status)}
                  endIcon={
                    <ChevronRightIcon
                      className={`${getPaymentChipIconColor(bankAccountsDetails?.currentPayroll?.Status)} payment-chip-icon`}
                    />
                  }
                />
              </div>

              {/* Add Payment row — always shown for multi-PM customers */}
              {isMultiPm && stripeFc && (
                <div
                  className="payment-option-row cursor-pointer mt-05"
                  onClick={handleConnectViaStripeFC}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleConnectViaStripeFC();
                  }}
                >
                  <div className="payment-option-icon-circle">
                    <AccountBalanceIcon className="payment-option-icon" />
                  </div>
                  <div className="payment-option-content">
                    <span className="payment-option-title">Add Payment</span>
                    <p className="payment-option-description">
                      Log in to your bank account to connect it as a payment method.
                    </p>
                  </div>
                  <ChevronRightIcon className="payment-option-chevron" />
                </div>
              )}

              {/* Non-default PM list */}
              {otherPms.length > 0 && (
                <div className="flex flex-col gap-05 mt-05">
                  {otherPms.map((pm) => (
                    <div key={pm.id} className="flex items-center justify-between py-075 px-1 border rounded-lg">
                      <div className="flex items-center gap-075">
                        {pm.type === "card" ? (
                          renderPaymentCard(pm.brand)
                        ) : (
                          <AccountBalanceIcon className="payment-icon" />
                        )}
                        <span className="para-body-m-medium color-text-body-primary capitalize">
                          {pm.type === "card"
                            ? `${pm.brand} •••• ${pm.lastFour}`
                            : `${pm.bankName} •••• ${pm.lastFour}`}
                        </span>
                      </div>
                      <MoreVertIcon
                        className="cursor-pointer color-text-body-secondary"
                        onClick={(e) => handlePmMenuOpen(e, pm)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* ACH upsell when on card */}
              {paymentTypes?.creditCard && !paymentTypes?.achData && !isMultiPm && (
                <div className="change-ach-container mt-1">
                  <div className="flex flex-col gap-025">
                    <p className="heading-h4-semibold">Avoid surcharge with ACH Direct Debit</p>
                    <p className="para-body-m-regular light-grey-text">
                      Changing takes less than a minute, and it's worth it.
                    </p>
                  </div>
                  <CustomButton label="Change to ACH" onClick={() => handlePaymentMethodClick("ach", false, true)} />
                </div>
              )}
            </div>
          ) : (
            /* Empty state */
            <div>
              <RiveAnimation
                style={{ marginLeft: "-20px", marginBottom: "-10px" }}
                src="/assets/animations/payment_method.riv"
                width="120px"
                height="132px"
              />
              <div className="heading-h1-semibold">Add payment method</div>
              <p className="para-body-m-regular color-text-heading-secondary mt-05">
                Set up your payment method to keep your operations running smoothly and avoid any interruptions. Your
                details are encrypted and safely stored for future use.
              </p>

              {stripeFc ? (
                <div className="flex flex-col mt-2">
                  <div className="flex flex-col gap-075 mb-05">
                    <p className="heading-h5-medium color-text-heading-primary">ACH direct debit</p>
                    <div className="border-b" />
                  </div>

                  <div
                    className="payment-option-row cursor-pointer"
                    onClick={handleConnectViaStripeFC}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleConnectViaStripeFC();
                    }}
                  >
                    <div className="payment-option-icon-circle">
                      <AccountBalanceIcon className="payment-option-icon" />
                    </div>
                    <div className="payment-option-content">
                      <div className="payment-option-title-row">
                        <span className="payment-option-title">Connect with Stripe</span>
                        <CustomChip label="Recommended" variant="filled" size="small" />
                      </div>
                      <p className="payment-option-description">
                        Log in to your bank account to connect it as a payment method.
                      </p>
                    </div>
                    <ChevronRightIcon className="payment-option-chevron" />
                  </div>

                  <div
                    className="payment-option-row cursor-pointer"
                    onClick={() => handlePaymentMethodClick("ach-form")}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ") handlePaymentMethodClick("ach-form");
                    }}
                  >
                    <div className="payment-option-icon-circle">
                      <AccountBalanceIcon className="payment-option-icon" />
                    </div>
                    <div className="payment-option-content">
                      <span className="payment-option-title">Enter bank details</span>
                      <p className="payment-option-description">Manually enter your bank details.</p>
                    </div>
                    <ChevronRightIcon className="payment-option-chevron" />
                  </div>

                  {paymentTypes.creditCard && (
                    <>
                      <div className="flex flex-col gap-075 mt-2 mb-05">
                        <p className="heading-h5-medium color-text-heading-primary">Other options</p>
                        <div className="border-b" />
                      </div>
                      <div
                        className="payment-option-row cursor-pointer"
                        onClick={() => handlePaymentMethodClick("card")}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" || e.key === " ") handlePaymentMethodClick("card");
                        }}
                      >
                        <div className="payment-option-icon-circle">
                          <CreditCardIcon className="payment-option-icon" />
                        </div>
                        <div className="payment-option-content">
                          <div className="payment-option-title-row">
                            <span className="payment-option-title">Credit / Debit Card</span>
                            <span className="para-body-s-medium color-text-body-primary">Incl. surcharge</span>
                          </div>
                          <p className="payment-option-description">
                            Pay with any major credit or debit card. No bank account linking required.
                          </p>
                        </div>
                        <ChevronRightIcon className="payment-option-chevron" />
                      </div>
                    </>
                  )}
                  <p className="para-body-s-regular color-text-body-primary mt-1">
                    Your payment information is encrypted and securely shared with Stripe for processing. We never store
                    your card or bank account information.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col border rounded-lg mt-1">
                  {paymentTypes.ach && (
                    <div
                      className="customer-payment-method-card cursor-pointer"
                      onClick={() => handlePaymentMethodClick("ach")}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") handlePaymentMethodClick("ach");
                      }}
                    >
                      <div className="flex items-center w-full gap-075">
                        <div className="payment-method-icon">
                          <AccountBalanceIcon className="payment-icon" />
                        </div>
                        <div className="payment-method-info">
                          <div className="payment-method-title">
                            <span className="heading-h5-semibold color-text-heading-primary">ACH Direct Debit</span>
                            <CustomChip label="Recommended" variant="filled" size="small" />
                          </div>
                        </div>
                        <ChevronRightIcon className="payment-method-arrow" />
                      </div>
                    </div>
                  )}
                  {paymentTypes.ach && paymentTypes.creditCard && <div className="border-b h-1p" />}
                  {paymentTypes.creditCard && (
                    <div
                      className="customer-payment-method-card-cc cursor-pointer"
                      onClick={() => handlePaymentMethodClick("card")}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") handlePaymentMethodClick("card");
                      }}
                    >
                      <div className="flex items-center w-full gap-075">
                        <div className="payment-method-icon">
                          <CreditCardIcon className="payment-icon" />
                        </div>
                        <div className="payment-method-info">
                          <div className="payment-method-title">
                            <span className="heading-h5-semibold color-text-heading-primary">Credit/Debit Card</span>
                            <span className="mt-2p para-body-s-medium color-text-body-primary">Incl. surcharge</span>
                          </div>
                        </div>
                        <ChevronRightIcon className="payment-method-arrow" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Context menu for non-default PMs */}
      <Menu anchorEl={pmMenuAnchor} open={Boolean(pmMenuAnchor)} onClose={handlePmMenuClose}>
        <MenuItem
          onClick={() => {
            handlePmMenuClose();
            handlePaymentMethodClick(selectedPm?.type === "card" ? "card" : "ach", true);
          }}
        >
          Update
        </MenuItem>
        <MenuItem onClick={handleMakeDefault}>Make Default</MenuItem>
        <MenuItem onClick={handleDetach} sx={{ color: "error.main" }}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}

export default PaymentMethods;
